import React from 'react';
import { createPortal } from 'react-dom';
import type { MemberNoteType } from '../../types/pointItemNote';

export type BulkGrantMode = 'points' | 'coins' | 'notes';

type Props = {
  open: boolean;
  mode: BulkGrantMode;
  selectedCount: number;
  onClose: () => void;
  /** 블럭(테이블)에만 반영 */
  onApplyToBlocks: () => void;
  /** 즉시 지급/저장 */
  onGrant: () => void;

  // points
  pointTypeLabel?: React.ReactNode;
  onOpenPointTypeSelect?: () => void;
  pointValue?: string;
  onPointValueChange?: (v: string) => void;
  pointMemo?: string;
  onPointMemoChange?: (v: string) => void;

  // coins
  coinAmount?: number;
  onCoinAmountChange?: (n: number) => void;
  coinStep?: number;
  coinMemo?: string;
  onCoinMemoChange?: (v: string) => void;

  // notes
  noteText?: string;
  onNoteTextChange?: (v: string) => void;
  noteType?: MemberNoteType;
  onNoteTypeCycle?: () => void;
};

const NOTE_TYPE_COLOR: Record<MemberNoteType, string> = {
  일반: '#B8B8B8',
  학습: '#4CC7AE',
  행동: '#FF376E',
};

const BulkGrantModal: React.FC<Props> = ({
  open,
  mode,
  selectedCount,
  onClose,
  onApplyToBlocks,
  onGrant,
  pointTypeLabel,
  onOpenPointTypeSelect,
  pointValue = '',
  onPointValueChange,
  pointMemo = '',
  onPointMemoChange,
  coinAmount = 0,
  onCoinAmountChange,
  coinStep = 10,
  coinMemo = '',
  onCoinMemoChange,
  noteText = '',
  onNoteTextChange,
  noteType = '일반',
  onNoteTypeCycle,
}) => {
  if (!open) return null;

  const title =
    mode === 'coins' ? '일괄 코인 지급' : mode === 'notes' ? '일괄 기록 작성' : '일괄 포인트 지급';
  const grantLabel =
    mode === 'coins' ? '코인 지급' : mode === 'notes' ? '기록 저장' : '포인트 지급';
  const applyLabel = mode === 'points' ? '블럭에 반영' : '테이블 반영';

  return createPortal(
    <div className="cp-pin-bulk-overlay" role="presentation" onClick={onClose}>
      <div
        className="cp-pin-bulk-modal"
        data-tutorial-pin="bulk-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-pin-bulk-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cp-pin-bulk-header">
          <h3 id="cp-pin-bulk-title">{title}</h3>
          <button type="button" className="cp-pin-modal-close" onClick={onClose} aria-label="닫기">
            ×
          </button>
        </header>

        <div className="cp-pin-bulk-body">
          {mode === 'points' ? (
            <>
              <div className="cp-pin-bulk-field">
                <label className="cp-pin-bulk-label">포인트 타입별 세트</label>
                <button
                  type="button"
                  className="cp-pin-bulk-select-btn"
                  onClick={onOpenPointTypeSelect}
                >
                  {pointTypeLabel || '포인트 타입 및 세트 선택'}
                </button>
              </div>
              <div className="cp-pin-bulk-field">
                <label className="cp-pin-bulk-label">지급 포인트</label>
                <div className="cp-pin-bulk-stepper">
                  <button
                    type="button"
                    className="cp-pin-bulk-step-btn"
                    onClick={() => {
                      const cur = Number.parseInt(pointValue, 10) || 0;
                      onPointValueChange?.(String(Math.max(0, cur - 1)));
                    }}
                    aria-label="감소"
                  >
                    −
                  </button>
                  <input
                    className="cp-pin-bulk-step-input"
                    type="number"
                    min={0}
                    value={pointValue}
                    onChange={(e) => onPointValueChange?.(e.target.value)}
                    placeholder="포인트"
                  />
                  <button
                    type="button"
                    className="cp-pin-bulk-step-btn"
                    onClick={() => {
                      const cur = Number.parseInt(pointValue, 10) || 0;
                      onPointValueChange?.(String(cur + 1));
                    }}
                    aria-label="증가"
                  >
                    +
                  </button>
                </div>
              </div>
              <div className="cp-pin-bulk-field">
                <label className="cp-pin-bulk-label">메모</label>
                <input
                  className="cp-pin-bulk-text-input"
                  type="text"
                  value={pointMemo}
                  onChange={(e) => onPointMemoChange?.(e.target.value)}
                  placeholder="메모 입력"
                />
              </div>
            </>
          ) : null}

          {mode === 'coins' ? (
            <>
              <div className="cp-pin-bulk-field">
                <label className="cp-pin-bulk-label">획득 코인</label>
                <div className="cp-pin-bulk-stepper">
                  <button
                    type="button"
                    className="cp-pin-bulk-step-btn"
                    onClick={() => onCoinAmountChange?.((coinAmount || 0) - coinStep)}
                    aria-label="감소"
                  >
                    −
                  </button>
                  <input
                    className="cp-pin-bulk-step-input is-accent"
                    type="number"
                    value={coinAmount}
                    onChange={(e) => onCoinAmountChange?.(Number(e.target.value) || 0)}
                  />
                  <button
                    type="button"
                    className="cp-pin-bulk-step-btn"
                    onClick={() => onCoinAmountChange?.((coinAmount || 0) + coinStep)}
                    aria-label="증가"
                  >
                    +
                  </button>
                </div>
              </div>
              <div className="cp-pin-bulk-field">
                <label className="cp-pin-bulk-label">메모</label>
                <input
                  className="cp-pin-bulk-text-input"
                  type="text"
                  value={coinMemo}
                  onChange={(e) => onCoinMemoChange?.(e.target.value)}
                  placeholder="메모 입력"
                />
              </div>
            </>
          ) : null}

          {mode === 'notes' ? (
            <>
              <div className="cp-pin-bulk-field">
                <label className="cp-pin-bulk-label">기록 내용</label>
                <textarea
                  className="cp-pin-bulk-textarea"
                  rows={4}
                  value={noteText}
                  onChange={(e) => onNoteTextChange?.(e.target.value)}
                  placeholder="멤버들에 대한 노트를 입력하세요"
                />
                <p className="cp-pin-bulk-note-hint" role="note">
                  주민번호·연락처·주소 등 민감한 개인정보는 입력하지 않도록 주의해 주세요.
                </p>
              </div>
              <div className="cp-pin-bulk-field">
                <label className="cp-pin-bulk-label">노트 타입</label>
                <button type="button" className="cp-pin-bulk-select-btn" onClick={onNoteTypeCycle}>
                  <span
                    className="cp-pin-bulk-note-dot"
                    style={{ background: NOTE_TYPE_COLOR[noteType] }}
                    aria-hidden
                  />
                  {noteType}
                </button>
              </div>
            </>
          ) : null}

          <p className="cp-pin-bulk-count">선택된 멤버: {selectedCount}명</p>
        </div>

        <footer className="cp-pin-bulk-footer" data-tutorial-pin="bulk-footer">
          <button
            type="button"
            className="cp-pin-modal-btn cp-pin-modal-btn--secondary"
            onClick={onClose}
          >
            취소
          </button>
          <button
            type="button"
            className="cp-pin-modal-btn cp-pin-modal-btn--primary"
            onClick={onApplyToBlocks}
          >
            {applyLabel}
          </button>
          <button
            type="button"
            className="cp-pin-modal-btn cp-pin-modal-btn--primary"
            onClick={onGrant}
          >
            {grantLabel}
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default BulkGrantModal;
